import type { AppError } from "@/shared/api";

/**
 * Контекст виклику — визначає очікуваність помилки й канал показу (ADR-0010
 * D4/D5). Той самий `404 master_not_found` означає різне залежно від операції:
 * deep-link load → панель «немає», PATCH → конкурентне видалення, DELETE → успіх.
 */
export type MasterErrorContext = "load" | "create" | "update" | "delete";

/**
 * Канал показу помилки:
 * - `field` — інлайн під конкретним полем (`field` заповнене: email/phone);
 * - `formName` — form-level `FormAlert` над групою ПІБ (422 `invalid_person_name`,
 *   контракт не атрибутує котре поле — R1);
 * - `notFound` — in-context панель «майстра вже немає» + «На список» (GET 404 / 400 invalid_id);
 * - `gone` — PATCH 404: toast(info) «майстра вже немає» + навігація на список;
 * - `deleted` — DELETE 404: трактуємо як успіх (вже видалено деінде);
 * - `blocked` — DELETE 409 `master_in_use`: in-context блок «спершу зніміть послуги»;
 * - `toast` — generic toast (неочікуване / 400 invalid_body / 403 / 413 / network / server).
 */
export type MasterErrorSurface =
  | "field"
  | "formName"
  | "notFound"
  | "gone"
  | "deleted"
  | "blocked"
  | "toast";

/** Поле для інлайн-помилки (`surface==='field'`). */
export type MasterErrorField =
  | "firstName"
  | "middleName"
  | "lastName"
  | "email"
  | "phone";

/**
 * i18n-ключі помилок майстрів (типобезпечний `t()`). Доменні `masters.errors.*`
 * поверх generic `errors.*` (ADR-0010 Follow-up).
 */
export type MasterErrorKey =
  | "masters.errors.firstNameRequired"
  | "masters.errors.lastNameRequired"
  | "masters.errors.nameTooLong"
  | "masters.errors.invalidName"
  | "masters.errors.invalidFirstName"
  | "masters.errors.invalidMiddleName"
  | "masters.errors.invalidLastName"
  | "masters.errors.invalidEmail"
  | "masters.errors.invalidPhone"
  | "masters.errors.inUse"
  | "masters.errors.notFound"
  | "errors.forbidden"
  | "errors.rateLimited"
  | "errors.server"
  | "errors.network"
  | "errors.unknown";

export interface MappedMasterError {
  surface: MasterErrorSurface;
  i18nKey: MasterErrorKey;
  /** Заповнене лише для `surface==='field'`. */
  field?: MasterErrorField;
  /** Варіант toast, коли `surface==='toast'|'gone'`. */
  toastVariant: "destructive" | "info";
}

/**
 * Тонкий адаптер `AppError → {surface, i18nKey, field?}` (ADR-0010 D9, патерн
 * `mapServiceError`). Гілка по `kind` (interceptor гарантує `AppError`) + код +
 * контекст виклику. Нормативна мапа — spec §7.4.
 *
 * Чиста функція — без router/store/toast/i18n; усе застосовує викликач
 * (composable). Юніт-тестована по кожній гілці.
 */
export function mapMasterError(
  e: AppError,
  ctx: MasterErrorContext,
): MappedMasterError {
  switch (e.kind) {
    case "validation":
      // 422 ПІБ — field-specific коди (tech-053): інлайн під конкретним полем.
      if (e.code === "invalid_first_name") {
        return { surface: "field", field: "firstName", i18nKey: "masters.errors.invalidFirstName", toastVariant: "destructive" };
      }
      if (e.code === "invalid_middle_name") {
        return { surface: "field", field: "middleName", i18nKey: "masters.errors.invalidMiddleName", toastVariant: "destructive" };
      }
      if (e.code === "invalid_last_name") {
        return { surface: "field", field: "lastName", i18nKey: "masters.errors.invalidLastName", toastVariant: "destructive" };
      }
      // 422 invalid_person_name — defensive fallback для неатрибутованого джерела
      // (hydrate-корупція, §6 spec): на write-path не виникає, але контракт робастний.
      if (e.code === "invalid_person_name") {
        return { surface: "formName", i18nKey: "masters.errors.invalidName", toastVariant: "destructive" };
      }
      if (e.code === "invalid_email") {
        return { surface: "field", field: "email", i18nKey: "masters.errors.invalidEmail", toastVariant: "destructive" };
      }
      if (e.code === "invalid_phone") {
        return { surface: "field", field: "phone", i18nKey: "masters.errors.invalidPhone", toastVariant: "destructive" };
      }
      // 400 invalid_id на GET/:id → трактуємо як «майстра немає» (панель).
      if (e.code === "invalid_id" && ctx === "load") {
        return { surface: "notFound", i18nKey: "masters.errors.notFound", toastVariant: "destructive" };
      }
      // 400 invalid_body / решта validation поза load — неочікувано (контракт/баг).
      return { surface: "toast", i18nKey: "errors.unknown", toastVariant: "destructive" };
    case "notFound":
      // Той самий 404 — різний канал за контекстом (spec §7.4).
      if (ctx === "update") {
        return { surface: "gone", i18nKey: "masters.errors.notFound", toastVariant: "info" };
      }
      if (ctx === "delete") {
        return { surface: "deleted", i18nKey: "masters.errors.notFound", toastVariant: "info" };
      }
      // load (та будь-що інше) → in-context панель.
      return { surface: "notFound", i18nKey: "masters.errors.notFound", toastVariant: "destructive" };
    case "conflict":
      // 409 master_in_use на delete → блок (FK RESTRICT, feature-006). Поза delete
      // не очікується — показуємо тим самим доменним текстом у toast.
      return {
        surface: ctx === "delete" ? "blocked" : "toast",
        i18nKey: "masters.errors.inUse",
        toastVariant: "destructive",
      };
    case "forbidden":
      return { surface: "toast", i18nKey: "errors.forbidden", toastVariant: "destructive" };
    case "rateLimited":
      return { surface: "toast", i18nKey: "errors.rateLimited", toastVariant: "destructive" };
    case "network":
      return { surface: "toast", i18nKey: "errors.network", toastVariant: "destructive" };
    case "server":
      // 413/415/500 (ADR-0010 status→kind).
      return { surface: "toast", i18nKey: "errors.server", toastVariant: "destructive" };
    default:
      return { surface: "toast", i18nKey: "errors.unknown", toastVariant: "destructive" };
  }
}
